/**
 * Dynamically imports the version-insensitive application bundle from a
 * version-sensitive bootstrap entry (see src/bootstrap.ts and
 * src/public-jan-bootstrap.ts), retrying once via a full reload if the
 * import itself fails.
 *
 * This covers the same failure mode staleChunkReload.ts recovers from for
 * lazy route chunks: a tab stays open across a deploy and the bootstrap's
 * target chunk hash no longer exists on the server, so the dynamic import
 * rejects with "Failed to fetch dynamically imported module" (Sentry
 * NOVONOTES-Y5A). staleChunkReload.ts can't help here because it's installed
 * *by* the module this function is trying to load.
 *
 * Deliberately framework-free (no Sentry/React/New Relic) so this logic stays
 * inside the tiny bootstrap chunk instead of pulling in the whole app graph
 * just to report the error. The NR agent is initialised by the app module this
 * function is trying to load, so bootstrap reloads cannot emit sw_lifecycle
 * PageActions. A failure that persists after the reload is left alone —
 * index.html's own 20s splash safety net covers the generic "app never
 * mounted" case, and console output is still visible for local debugging.
 */

// Intentionally the same sessionStorage key staleChunkReload.ts uses (not
// imported from there to avoid pulling in Logger/Sentry): sharing the guard
// window means a bootstrap-level reload also suppresses an immediate
// re-reload from staleChunkReload once the app module loads and installs it.
const RELOAD_AT_KEY = "novonote:stale-chunk-reload-at";
const RELOAD_LOOP_WINDOW_MS = 60_000;

function shouldAttemptReload(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_AT_KEY);
    if (!raw) return true;
    const lastReloadAt = Number(raw);
    if (!Number.isFinite(lastReloadAt)) return true;
    return Date.now() - lastReloadAt > RELOAD_LOOP_WINDOW_MS;
  } catch {
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — worst case this guard is skipped.
  }
}

export interface ImportAppOrReloadTarget {
  location: { reload: () => void };
  navigator: { onLine: boolean };
}

export function importAppOrReload(
  load: () => Promise<unknown>,
  target: ImportAppOrReloadTarget = window
): Promise<void> {
  return load()
    .then(() => undefined)
    .catch((error: unknown) => {
      console.error("[bootstrap] Failed to load application bundle", error);

      if (!target.navigator.onLine) {
        // Reloading cannot fetch anything new while offline — leave the
        // boot splash / blank page in place rather than trading it for a
        // browser error page.
        return;
      }

      if (!shouldAttemptReload()) {
        // Already reloaded recently and the import is still failing — stop
        // looping and let the 20s splash safety net (or a blank page) show.
        return;
      }

      markReloaded();
      target.location.reload();
    });
}
